'use client';

import { useState, useEffect } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const propertyTypes = [
  { name: 'Single-Family', icon: '🏠' },
  { name: 'Multi-Family', icon: '🏢' },
  { name: 'Commercial', icon: '🏬' },
  { name: 'HOA / Condo', icon: '🏘️' },
  { name: 'Short-Term Rental', icon: '🏡' },
  { name: 'Vacation', icon: '🌴' },
  { name: 'Mixed-Use', icon: '🏗️' },
  { name: 'Healthcare', icon: '🏥' },
];

const competitors = [
  { name: 'AppFolio', types: ['Multi-Family', 'Commercial'] },
  { name: 'Buildium', types: ['Multi-Family', 'HOA / Condo'] },
  { name: 'Yardi', types: ['Multi-Family', 'Commercial'] },
  { name: 'Spreadsheets', types: [] },
];

const UniversalPlatform = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('universal-platform');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="universal-platform" className="relative py-16 sm:py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gray-50 dark:bg-gradient-to-b dark:from-secondary-950 dark:via-secondary-900/50 dark:to-secondary-950" />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `
            linear-gradient(to right, #3b82f6 1px, transparent 1px),
            linear-gradient(to bottom, #3b82f6 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <h2
            className={cn(
              "font-playfair text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6",
              "transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
          >
            <span className="text-gray-900 dark:text-secondary-100">One Platform.</span>
            <br />
            <span className="bg-gradient-to-r from-primary-600 via-primary-500 to-primary-600 dark:from-primary-400 dark:via-primary-300 dark:to-primary-400 bg-clip-text text-transparent">
              Any Property.
            </span>
            <br />
            <span className="text-gray-900 dark:text-secondary-100">Finally.</span>
          </h2>
          <p
            className={cn(
              "max-w-2xl mx-auto text-lg text-gray-500 dark:text-secondary-400",
              "transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            Other platforms force you to choose. We built Axis to handle everything.
          </p>
        </div>

        {/* Property Type Grid */}
        <div
          className={cn(
            "grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-16",
            "transition-all duration-700 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: '200ms' }}
        >
          {propertyTypes.map((type, i) => (
            <div
              key={type.name}
              className={cn(
                "group relative p-4 sm:p-6 rounded-2xl text-center",
                "bg-white shadow-sm dark:bg-white/[0.03] dark:shadow-none border border-gray-200 dark:border-white/[0.08]",
                "hover:bg-primary-500/10 hover:border-primary-500/30",
                "transition-all duration-300"
              )}
              style={{ transitionDelay: `${300 + i * 50}ms` }}
            >
              <div className="text-2xl sm:text-4xl mb-2 sm:mb-3">{type.icon}</div>
              <div className="text-sm sm:text-base text-gray-800 dark:text-secondary-200 font-medium">{type.name}</div>
              <Check className="absolute top-3 right-3 w-5 h-5 text-primary-600 dark:text-primary-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>

        {/* Competitor Comparison */}
        <div
          className={cn(
            "bg-white dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.08] rounded-2xl p-4 sm:p-6 md:p-8",
            "transition-all duration-700 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: '600ms' }}
        >
          <h3 className="text-xl font-semibold text-gray-800 dark:text-secondary-200 mb-6 text-center">
            See how we compare
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-gray-200 dark:border-white/10">
                  <th className="text-left py-4 px-4 text-gray-500 dark:text-secondary-400 font-medium">Platform</th>
                  {propertyTypes.slice(0, 6).map((type) => (
                    <th key={type.name} className="py-4 px-2 text-center">
                      <span className="text-xl">{type.icon}</span>
                      <div className="text-xs text-gray-400 dark:text-secondary-500 mt-1 whitespace-nowrap">{type.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Axis Row - Highlighted */}
                <tr className="bg-primary-500/10 border-b border-primary-500/20">
                  <td className="py-4 px-4">
                    <span className="font-bold text-primary-600 dark:text-primary-400">Axis</span>
                  </td>
                  {propertyTypes.slice(0, 6).map((type) => (
                    <td key={type.name} className="py-4 px-2 text-center">
                      <Check className="w-5 h-5 text-primary-600 dark:text-primary-400 mx-auto" />
                    </td>
                  ))}
                </tr>

                {/* Competitor Rows */}
                {competitors.map((competitor) => (
                  <tr key={competitor.name} className="border-b border-gray-100 dark:border-white/5">
                    <td className="py-4 px-4 text-gray-500 dark:text-secondary-400">{competitor.name}</td>
                    {propertyTypes.slice(0, 6).map((type) => (
                      <td key={type.name} className="py-4 px-2 text-center">
                        {competitor.types.includes(type.name) ? (
                          <Check className="w-5 h-5 text-gray-400 dark:text-secondary-500 mx-auto" />
                        ) : (
                          <X className="w-5 h-5 text-gray-300 dark:text-secondary-700 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-gray-500 dark:text-secondary-500 text-sm mt-6">
            Stop juggling multiple platforms. Manage your entire portfolio from one place.
          </p>
        </div>
      </div>
    </section>
  );
};

export { UniversalPlatform };
